import { useState, useEffect } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import { supabase } from '../../lib/supabase'
import Modal from '../shared/Modal'
import TemplateForm from './TemplateForm'

export default function TemplatesTab({ selectedAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [deletingTemplate, setDeletingTemplate] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [filterAccountId, setFilterAccountId] = useState(selectedAccount?.WhatsAppAccountId || '')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchAccounts = async () => {
      const { data } = await supabase
        .from('WhatsAppAccounts')
        .select('WhatsAppAccountId, Name')
        .eq('IsDeleted', false)
        .order('Name')
      if (data) setAccounts(data)
    }
    fetchAccounts()
  }, [])

  useEffect(() => {
    if (selectedAccount) {
      setFilterAccountId(selectedAccount.WhatsAppAccountId)
    }
  }, [selectedAccount])

  const { data: templates, loading, error, refetch } = useSupabaseQuery('WhatsAppTemplates', {
    eq: { IsDeleted: false, ...(filterAccountId && { WhatsAppAccountId: filterAccountId }) },
    order: { column: 'Name', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('WhatsAppTemplates')

  const getAccountName = (accountId) => {
    return accounts.find(a => a.WhatsAppAccountId === accountId)?.Name || 'Unknown'
  }

  const filteredTemplates = templates?.filter(template => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      template.Name?.toLowerCase().includes(search) ||
      template.Language?.toLowerCase().includes(search)
    )
  })

  const handleCreate = async (formData) => {
    const accountId = filterAccountId || selectedAccount?.WhatsAppAccountId
    if (!accountId) {
      return { error: { message: 'Please select an account first' } }
    }
    const { data, error } = await insert({
      WhatsAppAccountId: accountId,
      ...formData,
      IsDeleted: false
    })
    if (!error && data) {
      setShowCreateModal(false)
      refetch()
    }
    return { data, error }
  }

  const handleUpdate = async (formData) => {
    const { data, error } = await update(
      editingTemplate.WhatsAppTemplateId,
      formData,
      'WhatsAppTemplateId'
    )
    if (!error) {
      setEditingTemplate(null)
      if (selectedTemplate?.WhatsAppTemplateId === editingTemplate.WhatsAppTemplateId) {
        setSelectedTemplate({ ...selectedTemplate, ...formData })
      }
      refetch()
    }
    return { data, error }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingTemplate.WhatsAppTemplateId, 'WhatsAppTemplateId')
    if (!error) {
      if (selectedTemplate?.WhatsAppTemplateId === deletingTemplate.WhatsAppTemplateId) {
        setSelectedTemplate(null)
      }
      setDeletingTemplate(null)
      refetch()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">WhatsApp Templates</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {filterAccountId ? `Templates for: ${getAccountName(Number(filterAccountId))}` : 'All templates'}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!filterAccountId && !selectedAccount}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          title={!filterAccountId && !selectedAccount ? 'Select an account to create templates' : ''}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Create Template</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account</label>
            <select
              value={filterAccountId}
              onChange={(e) => {
                setFilterAccountId(e.target.value)
                setSelectedTemplate(null)
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Accounts</option>
              {accounts.map(account => (
                <option key={account.WhatsAppAccountId} value={account.WhatsAppAccountId}>
                  {account.Name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Templates ({filteredTemplates?.length || 0})
              </h3>
            </div>
            {filteredTemplates && filteredTemplates.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                {filteredTemplates.map((template) => (
                  <li
                    key={template.WhatsAppTemplateId}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-4 cursor-pointer transition ${
                      selectedTemplate?.WhatsAppTemplateId === template.WhatsAppTemplateId
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {template.Name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Language: {template.Language}
                        </p>
                        {!filterAccountId && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            {getAccountName(template.WhatsAppAccountId)}
                          </p>
                        )}
                      </div>
                      {selectedTemplate?.WhatsAppTemplateId === template.WhatsAppTemplateId && (
                        <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No templates yet</p>
                {!filterAccountId && !selectedAccount && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Select an account to create templates
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Template Details */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {selectedTemplate.Name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Account: {getAccountName(selectedTemplate.WhatsAppAccountId)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
                    ID: {selectedTemplate.TemplateIdentifier}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingTemplate(selectedTemplate)}
                    className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingTemplate(selectedTemplate)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
                    <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                      {selectedTemplate.Language}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Template Identifier</label>
                    <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded font-mono text-xs">
                      {selectedTemplate.TemplateIdentifier}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Created</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTemplate.Created).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Updated</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTemplate.Updated).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="ml-3 text-sm text-blue-800 dark:text-blue-200">
                      WhatsApp templates must be registered and approved in Meta Business Manager before they can be sent.
                      Use the template name <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{selectedTemplate.Name}</code> when calling Send-Templated-WhatsApp (old name Send-WhatsApp-Templated still works as a dual-serving alias).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Template Selected</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select a template from the list to view details
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <Modal title="Create WhatsApp Template" onClose={() => setShowCreateModal(false)}>
          <TemplateForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {editingTemplate && (
        <Modal title="Edit WhatsApp Template" onClose={() => setEditingTemplate(null)}>
          <TemplateForm
            template={editingTemplate}
            onSubmit={handleUpdate}
            onCancel={() => setEditingTemplate(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {deletingTemplate && (
        <Modal title="Delete Template" onClose={() => setDeletingTemplate(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingTemplate.Name}</strong>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingTemplate(null)}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                {mutating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
